// Transient toast stack. Each entry removes itself after a fixed delay; nothing
// outside this hook ever needs to dismiss one.

import { useCallback, useEffect, useRef, useState } from 'react';

const TOAST_TTL_MS = 5000;

export default function useToasts() {
  const [toasts, setToasts] = useState([]);
  // Ids only have to be distinct within one mount, so a counter beats a random
  // string: no collision to reason about, and a readable value while debugging.
  const nextId = useRef(0);
  // Expiry timers still pending when the page goes away — firing them would set
  // state on an unmounted component.
  const timers = useRef(new Set());

  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current.clear();
    },
    [],
  );

  const pushToast = useCallback((toastOpts) => {
    nextId.current += 1;
    const id = `toast-${nextId.current}`;

    setToasts((cur) => [...cur, { ...toastOpts, id }]);

    const timer = setTimeout(() => {
      timers.current.delete(timer);
      setToasts((cur) => cur.filter((x) => x.id !== id));
    }, TOAST_TTL_MS);
    timers.current.add(timer);
  }, []);

  return { toasts, pushToast };
}
